import type { IMetadata } from '@adloid-technologies/discover-sdk';

/**
 * GMC Sierra LD (linkId 2379, project 59).
 *
 * Converted from the 1.0 repo's `src/metadata/data/metadata-2379.ts`
 * (`appData.visualiser.models[0]`, array-shaped) into the 2.0 object-keyed
 * `IMetadata` shape, following the conventions established for 2382/2377:
 * consolidate-or-split fingerprinting per entity, `autoIntelRPOCode` from
 * `brandItemCode`, real condition/isDefault/isStop threading on accessory
 * messages, model-level `hotspots` built from the default variant, and
 * `groupId`/`isDefault` derived from `removeAccessories` via connected
 * components (with single-select group categories normalised to the group's
 * dominant category — 1.0 sometimes mislabels one member).
 *
 * 1.0 ships a REAL `variant` filter here (relabelled `cabType`, `key`
 * 'variant') matching the actual Crew Cab / Regular Cab split — unlike
 * every other GMC project so far, whose engine/transmission filters are
 * generic unrelated boilerplate. Kept, and CONFIG is left visible because
 * of it (every other GMC migration hides it as empty).
 * 
 * Regular Cab variants share the bulk of their configurationIds with their
 * Crew Cab counterpart (AT4 <-> AT4 Regular Cab: 20/24 accessories shared;
 * SLE <-> SLE Regular Cab: all 29 shared) — a real, deliberate content
 * overlap in 1.0, not a data defect; consolidate-or-split handles it the
 * same as everywhere else.
 * 
 * Pricing: 1.0 ships all-zero/placeholder pricing for every variant
 * (showPrice:false in 1.0's own presentation config) — featureFlags.
 * showPrices is off and specifications.price is left blank rather than
 * showing a misleading "Starting from 0 MSRP".
 *
 * Generated — see the conversion notes in ui-metadata.ts.
 */
export const metadata: IMetadata = {
  models: {
    Sierra: {
      id: 'Sierra',
      lightingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      postProcessingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      backgroundIdentifer: 'dome',
      variants: {
        'denali-ultimate': {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '5821',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '5833',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '5822',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'bed_variant',
              sku: '5832',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'alloys',
              sku: '5820',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '5524',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        denali: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '5821',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '5912',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '5822',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'variants_parts',
              sku: '5903',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'bed_variant',
              sku: '5909',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'alloys',
              sku: '5911',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '5524',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        at4x: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '5821',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '5894',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '5822',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'variants_parts',
              sku: '5940',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'bed_variant',
              sku: '5944',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'alloys',
              sku: '5924',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '5524',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        at4: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '5821',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '5895',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '5822',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'variants_parts',
              sku: '5923',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'bed_variant',
              sku: '5928',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '5524',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        elevation: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '5821',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '5913',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '5822',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'variants_parts',
              sku: '5902',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'bed_variant',
              sku: '5943',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '5524',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        sle: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '5821',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '5890',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '5822',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'variants_parts',
              sku: '5898',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'bed_variant',
              sku: '5915',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '5524',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        'at4-regular-cab': {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '6110',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '6111',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '6126',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'variants_parts',
              sku: '6135',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'bed_variant',
              sku: '6173',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '5524',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        'elevation-regular-cab': {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '6110',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '6116',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '6126',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'variants_parts',
              sku: '6105',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'bed_variant',
              sku: '6127',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '5524',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        'sle-regular-cab': {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '6110',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '6115',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '6126',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '5524',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
      },
      backgrounds: {
        g0ZA968_RpUd: {
          isDefault: true,
          sku: '5524',
          project: '59',
        },
        g0ZA968_RpUe: {
          isDefault: false,
          sku: '3139',
          project: '32',
        },
      },
      ambientLighting: {
        ambientColors: '',
        ambientLightings: {},
        relevantVariants: [],
      },
      colors: {
        exterior: {
          iswqr3ta2smz: {
            sku: '5822',
            project: '59',
            relevantVariants: [
              'denali-ultimate',
            ],
          },
          '9dwvdtcubmni': {
            sku: '5830',
            project: '59',
            relevantVariants: [
              'denali-ultimate',
            ],
          },
          rvbult7msus2: {
            sku: '5823',
            project: '59',
            relevantVariants: [
              'denali-ultimate',
            ],
          },
          ldux7w9zx2tr: {
            sku: '5827',
            project: '59',
            relevantVariants: [
              'denali-ultimate',
            ],
          },
          ajnl9x8ueak8: {
            sku: '5829',
            project: '59',
            relevantVariants: [
              'denali-ultimate',
            ],
          },
          hqo2209i14mn: {
            sku: '5826',
            project: '59',
            relevantVariants: [
              'denali-ultimate',
            ],
          },
          ngqz3k7xopn9: {
            sku: '5822',
            project: '59',
            relevantVariants: [
              'denali',
            ],
          },
          rn1y9hhalv1a: {
            sku: '5830',
            project: '59',
            relevantVariants: [
              'denali',
            ],
          },
          '2wpu0f9z0xy2': {
            sku: '5823',
            project: '59',
            relevantVariants: [
              'denali',
            ],
          },
          '31lc3bcuz6ga': {
            sku: '5827',
            project: '59',
            relevantVariants: [
              'denali',
            ],
          },
          w8tm66d7rc8h: {
            sku: '5829',
            project: '59',
            relevantVariants: [
              'denali',
            ],
          },
          uenro76tzzk2: {
            sku: '5826',
            project: '59',
            relevantVariants: [
              'denali',
            ],
          },
          un2u7cg5fike: {
            sku: '5822',
            project: '59',
            relevantVariants: [
              'at4x',
            ],
          },
          rr3ex8ma7rbb: {
            sku: '5880',
            project: '59',
            relevantVariants: [
              'at4x',
            ],
          },
          '93y3jy3mvdzv': {
            sku: '5823',
            project: '59',
            relevantVariants: [
              'at4x',
            ],
          },
          '1gk32exc74gx': {
            sku: '5827',
            project: '59',
            relevantVariants: [
              'at4x',
            ],
          },
          abc6emjqxsgo: {
            sku: '5829',
            project: '59',
            relevantVariants: [
              'at4x',
            ],
          },
          bz0iajvp7zaq: {
            sku: '5881',
            project: '59',
            relevantVariants: [
              'at4x',
            ],
          },
          l2cnhaxojfbj: {
            sku: '5822',
            project: '59',
            relevantVariants: [
              'at4',
            ],
          },
          'l2cnhaxojfbj-at4-regular-cab': {
            sku: '6130',
            project: '59',
            relevantVariants: [
              'at4-regular-cab',
            ],
          },
          xi1iswcatix1: {
            sku: '5880',
            project: '59',
            relevantVariants: [
              'at4',
            ],
          },
          'xi1iswcatix1-at4-regular-cab': {
            sku: '6103',
            project: '59',
            relevantVariants: [
              'at4-regular-cab',
            ],
          },
          '59f9uyd0u1rn': {
            sku: '5823',
            project: '59',
            relevantVariants: [
              'at4',
            ],
          },
          '59f9uyd0u1rn-at4-regular-cab': {
            sku: '6126',
            project: '59',
            relevantVariants: [
              'at4-regular-cab',
            ],
          },
          '7yhc7i937bni': {
            sku: '5827',
            project: '59',
            relevantVariants: [
              'at4',
            ],
          },
          '7yhc7i937bni-at4-regular-cab': {
            sku: '6113',
            project: '59',
            relevantVariants: [
              'at4-regular-cab',
            ],
          },
          a1kpp98rucik: {
            sku: '5829',
            project: '59',
            relevantVariants: [
              'at4',
            ],
          },
          'a1kpp98rucik-at4-regular-cab': {
            sku: '6123',
            project: '59',
            relevantVariants: [
              'at4-regular-cab',
            ],
          },
          '2wbo981nokwr': {
            sku: '5881',
            project: '59',
            relevantVariants: [
              'at4',
            ],
          },
          '2wbo981nokwr-at4-regular-cab': {
            sku: '6109',
            project: '59',
            relevantVariants: [
              'at4-regular-cab',
            ],
          },
          hxlzuoifin7b: {
            sku: '5822',
            project: '59',
            relevantVariants: [
              'elevation',
            ],
          },
          'hxlzuoifin7b-elevation-regular-cab': {
            sku: '6130',
            project: '59',
            relevantVariants: [
              'elevation-regular-cab',
            ],
          },
          inrrvsldvwfa: {
            sku: '5880',
            project: '59',
            relevantVariants: [
              'elevation',
            ],
          },
          'inrrvsldvwfa-elevation-regular-cab': {
            sku: '6103',
            project: '59',
            relevantVariants: [
              'elevation-regular-cab',
            ],
          },
          '0ad0ux6wzz1e': {
            sku: '5823',
            project: '59',
            relevantVariants: [
              'elevation',
            ],
          },
          '0ad0ux6wzz1e-elevation-regular-cab': {
            sku: '6126',
            project: '59',
            relevantVariants: [
              'elevation-regular-cab',
            ],
          },
          xrb4gsuqlnnn: {
            sku: '5827',
            project: '59',
            relevantVariants: [
              'elevation',
            ],
          },
          'xrb4gsuqlnnn-elevation-regular-cab': {
            sku: '6113',
            project: '59',
            relevantVariants: [
              'elevation-regular-cab',
            ],
          },
          ok9cu5jrbr38: {
            sku: '5829',
            project: '59',
            relevantVariants: [
              'elevation',
            ],
          },
          'ok9cu5jrbr38-elevation-regular-cab': {
            sku: '6123',
            project: '59',
            relevantVariants: [
              'elevation-regular-cab',
            ],
          },
          aqvfqfntmzdj: {
            sku: '5881',
            project: '59',
            relevantVariants: [
              'elevation',
            ],
          },
          'aqvfqfntmzdj-elevation-regular-cab': {
            sku: '6109',
            project: '59',
            relevantVariants: [
              'elevation-regular-cab',
            ],
          },
          '7eez7nplog7a': {
            sku: '5822',
            project: '59',
            relevantVariants: [
              'sle',
            ],
          },
          '7eez7nplog7a-sle-regular-cab': {
            sku: '6130',
            project: '59',
            relevantVariants: [
              'sle-regular-cab',
            ],
          },
          dtjmzz3bsfrl: {
            sku: '5880',
            project: '59',
            relevantVariants: [
              'sle',
            ],
          },
          'dtjmzz3bsfrl-sle-regular-cab': {
            sku: '6103',
            project: '59',
            relevantVariants: [
              'sle-regular-cab',
            ],
          },
          ruym5z4zk7bp: {
            sku: '5823',
            project: '59',
            relevantVariants: [
              'sle',
            ],
          },
          'ruym5z4zk7bp-sle-regular-cab': {
            sku: '6126',
            project: '59',
            relevantVariants: [
              'sle-regular-cab',
            ],
          },
          svfkt34o1vbs: {
            sku: '5827',
            project: '59',
            relevantVariants: [
              'sle',
            ],
          },
          'svfkt34o1vbs-sle-regular-cab': {
            sku: '6113',
            project: '59',
            relevantVariants: [
              'sle-regular-cab',
            ],
          },
          '2h6rvjratd39': {
            sku: '5829',
            project: '59',
            relevantVariants: [
              'sle',
            ],
          },
          '2h6rvjratd39-sle-regular-cab': {
            sku: '6123',
            project: '59',
            relevantVariants: [
              'sle-regular-cab',
            ],
          },
          ys6fc5ecm0gr: {
            sku: '5881',
            project: '59',
            relevantVariants: [
              'sle',
            ],
          },
          'ys6fc5ecm0gr-sle-regular-cab': {
            sku: '6109',
            project: '59',
            relevantVariants: [
              'sle-regular-cab',
            ],
          },
        },
        interior: {
          wghqimbkxd6j: {
            sku: '5833',
            project: '59',
            relevantVariants: [
              'denali-ultimate',
            ],
            notCompatibleExteriorColors: [],
            affectedInteractionOnApply: {
              ywx48hyqwid8: { add: false, snapToLastFrame: true },
            },
          },
          '1lyzdw5is5pe': {
            sku: '5912',
            project: '59',
            relevantVariants: [
              'denali',
            ],
            notCompatibleExteriorColors: [],
            affectedInteractionOnApply: {
              '8eaoh15qfyy8': { add: false, snapToLastFrame: true },
            },
          },
          z3kg3f2fz96g: {
            sku: '5894',
            project: '59',
            relevantVariants: [
              'at4x',
            ],
            notCompatibleExteriorColors: [],
            affectedInteractionOnApply: {
              jkzo5nb4kcu8: { add: false, snapToLastFrame: true },
            },
          },
          icae047aermx: {
            sku: '5895',
            project: '59',
            relevantVariants: [
              'at4',
            ],
            notCompatibleExteriorColors: [],
            affectedInteractionOnApply: {
              ttgcv0vc2ypv: { add: false, snapToLastFrame: true },
            },
          },
          'icae047aermx-at4-regular-cab': {
            sku: '6111',
            project: '59',
            relevantVariants: [
              'at4-regular-cab',
            ],
            notCompatibleExteriorColors: [],
            affectedInteractionOnApply: {
              ttgcv0vc2ypv: { add: false, snapToLastFrame: true },
            },
          },
          '77v7ehz8c21y': {
            sku: '5913',
            project: '59',
            relevantVariants: [
              'elevation',
            ],
            notCompatibleExteriorColors: [],
            affectedInteractionOnApply: {
              '80whon9hvq2k': { add: false, snapToLastFrame: true },
            },
          },
          '77v7ehz8c21y-elevation-regular-cab': {
            sku: '6116',
            project: '59',
            relevantVariants: [
              'elevation-regular-cab',
            ],
            notCompatibleExteriorColors: [],
            affectedInteractionOnApply: {
              '80whon9hvq2k': { add: false, snapToLastFrame: true },
            },
          },
          '5vktfxushcn42': {
            sku: '5921',
            project: '59',
            relevantVariants: [
              'elevation',
            ],
            notCompatibleExteriorColors: [],
            affectedInteractionOnApply: {
              '80whon9hvq2k': { add: false, snapToLastFrame: true },
            },
          },
          '5vktfxushcn42-elevation-regular-cab': {
            sku: '6108',
            project: '59',
            relevantVariants: [
              'elevation-regular-cab',
            ],
            notCompatibleExteriorColors: [],
            affectedInteractionOnApply: {
              '80whon9hvq2k': { add: false, snapToLastFrame: true },
            },
          },
          '5vktfxuscn42': {
            sku: '5890',
            project: '59',
            relevantVariants: [
              'sle',
            ],
            notCompatibleExteriorColors: [],
            affectedInteractionOnApply: {
              nw7ayiomff7o: { add: false, snapToLastFrame: true },
            },
          },
          '5vktfxuscn42-sle-regular-cab': {
            sku: '6115',
            project: '59',
            relevantVariants: [
              'sle-regular-cab',
            ],
            notCompatibleExteriorColors: [],
            affectedInteractionOnApply: {
              nw7ayiomff7o: { add: false, snapToLastFrame: true },
            },
          },
        },
      },
      features: {
        ovv9q52phjv5: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'denali-ultimate',
          ],
          messages: [
            {
              message: 'SHADER_Front_Grille_Denali',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front_Grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        f9g0udkfgg1s: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'denali-ultimate',
          ],
          messages: [
            {
              message: 'SHADER_22inch_wheel',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        aqhtccsq854s: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'denali-ultimate',
          ],
          messages: [
            {
              message: 'Power_Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        uuwg2vhs5qmz: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'denali-ultimate',
          ],
          messages: [
            {
              message: '6_2L_Engine',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '422mk8nah7jh': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali-ultimate',
          ],
          messages: [
            {
              message: 'SHADER_Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        k0yx9f0088x6: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali-ultimate',
          ],
          messages: [
            {
              message: 'SHADER_Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '3ofodapu2ddp': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali-ultimate',
          ],
          messages: [
            {
              message: 'SHADER_HUD',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Heads_Up_Display',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '2xmnp8rwibts': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali-ultimate',
          ],
          messages: [
            {
              message: 'Interior',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        hq5xn1dxclar: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali-ultimate',
          ],
          messages: [
            {
              message: 'Seat_Pattern',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        qvs3dqxessnb: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali-ultimate',
          ],
          messages: [
            {
              message: 'SHADER_Boss_Speaker',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Bose_Speaker',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          // Sound System's shot needs the door closed - otherwise the shader
          // renders disconnected from the body when a door happens to be
          // open (matches Yukon's ORVM fix pattern).
          affectedInteractionOnApply: {
            ywx48hyqwid8: { add: false, snapToLastFrame: true },
          },
        },
        ie634vsmq0j6: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'SHADER_Front_Grille_Denali',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front_Grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '3ihc3y18u7zd': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'SHADER_20inch_wheel',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '6q2cg6iiqn3h': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'Power_Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        ynodrohzcw0i: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: '6_2L_Engine',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        jf4fjyd9qle0: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'SHADER_GMC MultiPro',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Retractable_Power_Steps',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        givzqghx1l82: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'SHADER_Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        bz0qkr3fp1sc: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'SHADER_Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        m0ndpn2lkd0f: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'Interior',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        m52hpo7jkq9e: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'SHADER_wireless_charger',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wireless_charging',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        b5uhkotwnzjn: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'SHADER_Boss_Speaker',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Bose_Speaker',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            '8eaoh15qfyy8': { add: false, snapToLastFrame: true },
          },
        },
        ptmckza3t31n: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4x',
          ],
          messages: [
            {
              message: 'SHADER_Front_Bumper_AT4X',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front_Bumper',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        nv6lzeqsok8w: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4x',
          ],
          messages: [
            {
              message: 'SHADER_18inch_wheel_AT4X',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        fpi9zqhbdcvv: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4x',
          ],
          messages: [
            {
              message: 'Power_Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        e31dagcaee09: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4x',
          ],
          messages: [
            {
              message: '6_2L_Engine',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        lkuhj3fvxjx9: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4x',
          ],
          messages: [
            {
              message: 'SHADER_Tailgate',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Tailgate',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            '7d69b0ehn4v1': { add: false, snapToLastFrame: true },
          },
        },
        gk2otd6c69sm: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4x',
          ],
          messages: [
            {
              message: '2_inch_Suspension_Lift',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        tnagptikzt93: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4x',
          ],
          messages: [
            {
              message: 'SHADER_Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '3eo3tzxeu3a8': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4x',
          ],
          messages: [
            {
              message: 'SHADER_Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        tjazpuwqk701: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4x',
          ],
          messages: [
            {
              message: 'Interior',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        io06xfbcm17q: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4x',
          ],
          messages: [
            {
              message: 'SHADER_Dash control',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Heated_and_Ventilated Seats',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        awla0dgc0vgs: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4x',
          ],
          messages: [
            {
              message: 'SHADER_Boss_Speaker',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Bose_Speaker',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            jkzo5nb4kcu8: { add: false, snapToLastFrame: true },
          },
        },
        awla0drrc0vgs: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4x',
          ],
          messages: [
            {
              message: 'Driving_modes',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '3mdpbwhqmsv1': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'SHADER_Fog_Lamp',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'LED_Fog_Lamps',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '3mdpbwhqmsv1-at4-regular-cab': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4-regular-cab',
          ],
          messages: [
            {
              message: 'SHADER_Fog_Lamp',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'LED_Fog_Lamps',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        g7w0pdbil08m: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'SHADER_18inch_wheel',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'g7w0pdbil08m-at4-regular-cab': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4-regular-cab',
          ],
          messages: [
            {
              message: 'SHADER_18inch_wheel',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '7cl358iacwqj': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'SHADER_Front_Face',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front_Hooks',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '7cl358iacwqj-at4-regular-cab': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4-regular-cab',
          ],
          messages: [
            {
              message: 'SHADER_Front_Face',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front_Hooks',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        e31dfeagcaee09: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: '2_inch_Suspension_Lift',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'e31dfeagcaee09-at4-regular-cab': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4-regular-cab',
          ],
          messages: [
            {
              message: '2_inch_Suspension_Lift',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        c9gqpi62g18z: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'SHADER_Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'c9gqpi62g18z-at4-regular-cab': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4-regular-cab',
          ],
          messages: [
            {
              message: 'SHADER_Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        tsxkgkfoxdo6: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'SHADER_Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'tsxkgkfoxdo6-at4-regular-cab': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4-regular-cab',
          ],
          messages: [
            {
              message: 'SHADER_Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        muey3bbku37k: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'Interior',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'muey3bbku37k-at4-regular-cab': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4-regular-cab',
          ],
          messages: [
            {
              message: 'Interior',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        z7m52iyopf8c: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: '2_Speed_Transfer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'z7m52iyopf8c-at4-regular-cab': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4-regular-cab',
          ],
          messages: [
            {
              message: '2_Speed_Transfer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        aj49xu9d3hac: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'elevation',
          ],
          messages: [
            {
              message: 'SHADER_Fog_Lamp',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'LED_Fog_Lamps',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'aj49xu9d3hac-elevation-regular-cab': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'elevation-regular-cab',
          ],
          messages: [
            {
              message: 'SHADER_Fog_Lamp',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'LED_Fog_Lamps',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '4r35jcvwymvo': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'elevation',
          ],
          messages: [
            {
              message: 'SHADER_18inch_wheel',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '4r35jcvwymvo-elevation-regular-cab': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'elevation-regular-cab',
          ],
          messages: [
            {
              message: 'SHADER_18inch_wheel',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        feyns0167q1m: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'elevation',
          ],
          messages: [
            {
              message: 'Front_Grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Front_Grille_Elevatiion',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'feyns0167q1m-elevation-regular-cab': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'elevation-regular-cab',
          ],
          messages: [
            {
              message: 'Front_Grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Front_Grille_Elevatiion',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '3a79ezcnnbft': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'elevation',
          ],
          messages: [
            {
              message: 'SHADER_Bed',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Bed',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '3a79ezcnnbft-elevation-regular-cab': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'elevation-regular-cab',
          ],
          messages: [
            {
              message: 'SHADER_Bed',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Bed',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        zfyfm5c98h57: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'elevation',
          ],
          messages: [
            {
              message: 'SHADER_Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'zfyfm5c98h57-elevation-regular-cab': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'elevation-regular-cab',
          ],
          messages: [
            {
              message: 'SHADER_Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '7mzvb9y21623': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'elevation',
          ],
          messages: [
            {
              message: 'SHADER_Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '7mzvb9y21623-elevation-regular-cab': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'elevation-regular-cab',
          ],
          messages: [
            {
              message: 'SHADER_Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '56b99wbs39ki': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'sle',
          ],
          messages: [
            {
              message: 'SHADER_18inch_wheel',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '56b99wbs39ki-sle-regular-cab': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'sle-regular-cab',
          ],
          messages: [
            {
              message: 'SHADER_18inch_wheel',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        slfnh3hjd6s8: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'sle',
          ],
          messages: [
            {
              message: 'Front_Bumper',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Front_Bumper_SLE',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'slfnh3hjd6s8-sle-regular-cab': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'sle-regular-cab',
          ],
          messages: [
            {
              message: 'Front_Bumper',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Front_Bumper_SLE',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        e31dfagcaee09: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'sle',
          ],
          messages: [
            {
              message: 'SHADER_Bed',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Bed',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'e31dfagcaee09-sle-regular-cab': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'sle-regular-cab',
          ],
          messages: [
            {
              message: 'SHADER_Bed',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Bed',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        av8ukajwzjsm: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'sle',
          ],
          messages: [
            {
              message: 'Cargo_Tie_Downs',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'av8ukajwzjsm-sle-regular-cab': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'sle-regular-cab',
          ],
          messages: [
            {
              message: 'Cargo_Tie_Downs',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        e31dagcaeee09: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'sle',
          ],
          messages: [
            {
              message: 'SHADER_Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'e31dagcaeee09-sle-regular-cab': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'sle-regular-cab',
          ],
          messages: [
            {
              message: 'SHADER_Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        e31dagccaeee09: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'sle',
          ],
          messages: [
            {
              message: 'SHADER_Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'e31dagccaeee09-sle-regular-cab': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'sle-regular-cab',
          ],
          messages: [
            {
              message: 'SHADER_Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
      },
      accessories: {
        oxqwgswyt5gb: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Denali_Ultimate_EXT_Variant',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali-ultimate',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        w0vis6o69vtu: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Denali_Ultimate_Short_Bed',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali-ultimate',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '57psr5kkkly3': {
          view: 'interior',
          groupId: 'denali-ultimate-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Floor_Mat_Carpet',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali-ultimate',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '332xvlecu8zd': {
          view: 'exterior',
          groupId: 'denali-ultimate-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Tailgate_Pro_ULT_Denali',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali-ultimate',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        oxydwt1p6q9f: {
          view: 'interior',
          groupId: 'denali-ultimate-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Floor_Mat_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_FLOOR',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali-ultimate',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '98uc9rc3z96q': {
          view: 'exterior',
          groupId: 'denali-ultimate-g1',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Tailgate_Std_ULT_Denali',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_TAILGATE',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {
            '2blnjd8gu2v1': {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali-ultimate',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        gg4svo5rq6za: {
          view: 'exterior',
          groupId: 'denali-ultimate-g2',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'GMC_MultiPro_Power_Steps',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Assist_Steps',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_GMC MultiPro',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            ywx48hyqwid8: {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali-ultimate',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        jfprn5ll1q01: {
          view: 'exterior',
          groupId: 'denali-ultimate-g2',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Camera_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            ywx48hyqwid8: {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali-ultimate',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        gisdi9lmd3sl: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Bed_Mat_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Bed_Mat_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Bed_Mat_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali-ultimate',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        qpcnnd9zo4jh: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Illuminated_GMC_Logo_Black_DENALI',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Illuminated_GMC_Logo_Black_DENALI',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Illuminated_GMC_Logo_Black_DENALI',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali-ultimate',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '9rpr1yy99434': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Led_Puddle_Light_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Led_Puddle_Light_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Led_Puddle_Light_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali-ultimate',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        hhbzhza95ou3: {
          view: 'interior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Pedal_Covers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Pedal_Covers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Pedal_Covers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali-ultimate',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        hhl4itlcpms4: {
          view: 'interior',
          groupId: 'denali-ultimate-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Floor_Liners_CC_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Floor_Liners_CC_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Floor_Liners_CC_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali-ultimate',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8mc6jgrcw8a6': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Hard_Folding_Tonneau_Cover_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Hard_Folding_Tonneau_Cover_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Hard_Folding_Tonneau_Cover_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali-ultimate',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        agc75a70vder: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali-ultimate',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8kdvr886cyd5': {
          view: 'exterior',
          groupId: 'denali-ultimate-g3',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Bed_Carbon_Pro',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali-ultimate',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist2outtttdgpfud: {
          view: 'exterior',
          groupId: 'denali-ultimate-g3',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Bed_Liner_Spray_on',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Cargo_Bed',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali-ultimate',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ridxq0jjfq6j: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Denali_EXT_Variant',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        yg11hrtg0w1p: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Denali_Short_Bed',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        r6pzsgbyb3dd: {
          view: 'interior',
          groupId: 'denali-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Floor_Mat_Carpet_Brown',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_FLOOR',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        r4nmd5ed5jcz: {
          view: 'exterior',
          groupId: 'denali-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Tailgate_Pro_Denali',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        uor1deqq6b3q: {
          view: 'interior',
          groupId: 'denali-g2',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Assist_Steps_BVT',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        y0n8v4i02n1m: {
          view: 'exterior',
          groupId: 'denali-g1',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Tailgate_Std_Denali',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_TAILGATE',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Std_Tailgate',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            qwl8becl9sgr: {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        b5abq5t1vf20: {
          view: 'exterior',
          groupId: 'denali-g3',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Bed_Liner_Spray_on',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        e8gz1yuapj7p: {
          view: 'exterior',
          groupId: 'denali-g2',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Camera_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            '8eaoh15qfyy8': {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        efio0cm77wbz: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Bed_Mat_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Bed_Mat_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Bed_Mat_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        u6lykoko82sa: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Illuminated_GMC_Logo_Black_DENALI',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Illuminated_GMC_Logo_Black_DENALI',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Illuminated_GMC_Logo_Black_DENALI',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        edpc1ixph8y0: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Led_Puddle_Light_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Led_Puddle_Light_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Led_Puddle_Light_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        vx92rnkv38x4: {
          view: 'interior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Pedal_Covers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Pedal_Covers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Pedal_Covers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        u4454t54sdud: {
          view: 'interior',
          groupId: 'denali-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Floor_Liners_CC_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Floor_Liners_CC_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Floor_Liners_CC_GMC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        y22i0rjx2iqu: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Hard_Folding_Tonneau_Cover_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Hard_Folding_Tonneau_Cover_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Hard_Folding_Tonneau_Cover_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        wbjjn1z62bk2: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist2outtudgpfud: {
          view: 'exterior',
          groupId: 'denali-g3',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Bed_Carbon_Pro',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Cargo_Bed',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist2outuudgpfud: {
          view: 'exterior',
          groupId: 'denali-g4',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Technology_Package_Denali',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist2outyudgpfud: {
          view: 'exterior',
          groupId: 'denali-g4',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Technology_Package_AT4_Denali',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist2outtpptdgpfud: {
          view: 'interior',
          groupId: 'denali-g3',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'Bed_Liner_Spray_on',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Cargo_Bed',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        rvc7zfovo8l63: {
          view: 'exterior',
          groupId: 'denali-g2',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'GMC_MultiPro_Power_Steps',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Assist_Steps',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_GMC MultiPro',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            '8eaoh15qfyy8': {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        mo9mwd103t3y: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'AT4X_EXT_Variant',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4x',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        qzyjhtvtvyz4: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'AT4X_Short_Bed',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4x',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        yg2c0vrjbifv: {
          view: 'interior',
          groupId: 'at4x-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Floor_Mat_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4x',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        o0nl62zzspiq: {
          view: 'exterior',
          // 'at4x-g1' now scoped to just this Tailgate pair - it used to
          // also be shared by the unrelated Steps and AEV Parts pairs below,
          // and since multiple isDefault:true accessories shared one group,
          // whichever applied last (file order) evicted this one via the
          // SDK's group-exclusivity cleanup - tailgate absent on load.
          groupId: 'at4x-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Tailgate_Pro_AT4X',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4x',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        pgakka1noktm: {
          view: 'interior',
          // Was 'at4x-g0', colliding with the unrelated Floor_Mat_Liners/
          // Floor Liners Black pair - both isDefault:true in the same group
          // meant this one's own group-exclusivity cleanup (applied later,
          // file order) silently reversed the floor mat default on load.
          groupId: 'at4x-g4',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Bed_Liner_Spray_on',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4x',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        o5w5w6gc2y8a: {
          view: 'exterior',
          groupId: 'at4x-g1', // Tailgate pair, paired with o0nl62zzspiq
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Tailgate_Std_AT4X',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_TAILGATE',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Tailgate',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            '7d69b0ehn4v1': {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4x',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        rvc7zfvo8l63: {
          view: 'exterior',
          groupId: 'at4x-g2', // Steps pair - was 'at4x-g1', see o0nl62zzspiq's comment
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'GMC_MultiPro_Power_Steps',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Assist_Steps',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_GMC MultiPro',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            jkzo5nb4kcu8: {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4x',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        u2lv72yhjvji: {
          view: 'exterior',
          groupId: 'at4x-g2', // Steps pair, paired with rvc7zfvo8l63
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Camera_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            jkzo5nb4kcu8: {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4x',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '9kr3yvmlgyna': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Bed_Mat_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Bed_Mat_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Bed_Mat_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4x',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        b6lmgadbw6d0: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Illuminated_GMC_Logo_Black_AT4X_AEV',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Illuminated_GMC_Logo_Black_AT4X_AEV',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Illuminated_GMC_Logo_Black_AT4X_AEV',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4x',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        bfe0tnmjue5h: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Led_Puddle_Light_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Led_Puddle_Light_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Led_Puddle_Light_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4x',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        o4zttd5xeiwl: {
          view: 'interior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Pedal_Covers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Pedal_Covers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Pedal_Covers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4x',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        u15xauvb0ygj: {
          view: 'interior',
          groupId: 'at4x-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Floor_Liners_CC_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Floor_Liners_CC_GMC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Floor_Liners_CC_GMC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4x',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        yhj2u5lbn0l6: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Hard_Folding_Tonneau_Cover_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Hard_Folding_Tonneau_Cover_CC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Hard_Folding_Tonneau_Cover_CC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4x',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        pi3b11cu1nvq: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4x',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist2outhtdgpfud: {
          view: 'exterior',
          groupId: 'at4x-g3', // AEV Parts pair - was 'at4x-g1', see o0nl62zzspiq's comment
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_AEV_Parts',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4x',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist2outtodgpfud: {
          view: 'exterior',
          groupId: 'at4x-g3', // AEV Parts pair, paired with ist2outhtdgpfud
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_AEV_Parts',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {
            '7d69b0ehn4v1': {
              add: false,
              snapToLastFrame: true,
            },
            jkzo5nb4kcu8: {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4x',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        yxm1e6rw13fe: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'AT4_EXT_Variant',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        fouew54veqvs: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'AT4_Short_Bed',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'fouew54veqvs-at4-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'AT4_STD_Bed',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        g6fl3n7ykrq5: {
          view: 'interior',
          groupId: 'at4-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Floor_Mat_Rubber',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'g6fl3n7ykrq5-at4-regular-cab': {
          view: 'interior',
          groupId: 'at4-regular-cab-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Floor_Mat_Rubber',
              identifiers: [
                'common_exterior',
                'optional',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '6yvi6vsvq7s9': {
          view: 'exterior',
          groupId: 'at4-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Tailgate_Pro',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '6yvi6vsvq7s9-at4-regular-cab': {
          view: 'exterior',
          groupId: 'at4-regular-cab-g1',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Tailgate_Pro',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_TAILGATE',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Tailgate',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            vbjrc2b3qn47: { add: false, snapToLastFrame: true },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '74ry84y59ibg': {
          view: 'interior',
          groupId: 'at4-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Floor_Mat_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_FLOOR',
              identifiers: [
                'common_exterior',
                'optional',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Floor_Liner',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '74ry84y59ibg-at4-regular-cab': {
          view: 'interior',
          groupId: 'at4-regular-cab-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Floor_Mat_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_FLOOR',
              identifiers: [
                'common_exterior',
                'optional',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Floor_Liner',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        zvaaxlwqe4qx: {
          view: 'exterior',
          groupId: 'at4-g1',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Tailgate_Std',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_TAILGATE',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Tailgate',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'zvaaxlwqe4qx-at4-regular-cab': {
          view: 'exterior',
          groupId: 'at4-regular-cab-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Tailgate_Std',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        kviwsh9jczd9: {
          view: 'exterior',
          groupId: 'at4-regular-cab-g2',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Machined_Aluminum_Dark_Painted_Pockets',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'kviwsh9jczd9-at4': {
          view: 'exterior',
          // Was 'at4-regular-cab-g2', the same group as the shared default
          // above - since that default's OWN groupId only pairs with
          // at4-regular-cab's wheel (es6d42r8m4tu-at4-regular-cab), plain
          // AT4's own wheel (es6d42r8m4tu, groupId 'at4-g2') was never in
          // the same group as its default, so picking it never evicted the
          // default - both rendered at once, visually overlapping.
          groupId: 'at4-g2',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Machined_Aluminum_Dark_Painted_Pockets',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        wu8y5uoc1ioq: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Camera_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            ttgcv0vc2ypv: {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'wu8y5uoc1ioq-at4-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Camera_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            ttgcv0vc2ypv: {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '7o58kl7brwfj': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Bed_Mat_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Bed_Mat_CC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Bed_Mat_CC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '7o58kl7brwfj-at4-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Bed_Mat_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Bed_Mat_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Bed_Mat_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        wh63po09fmaw: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Illuminated_GMC_Logo_Black_SLT_AT4',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Illuminated_GMC_Logo_Black_SLT_AT4',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Illuminated_GMC_Logo_Black_SLT_AT4',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'wh63po09fmaw-at4-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Illuminated_GMC_Logo_Black_SLT_AT4_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Illuminated_GMC_Logo_Black_SLT_AT4_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Illuminated_GMC_Logo_Black_SLT_AT4_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        wbxrfr2kxwji: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Led_Puddle_Light_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Led_Puddle_Light_GMC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Led_Puddle_Light_GMC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'wbxrfr2kxwji-at4-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Led_Puddle_Light_GMC_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Led_Puddle_Light_GMC_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Led_Puddle_Light_GMC_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '4suumceyxio5': {
          view: 'interior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Pedal_Covers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Pedal_Covers',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Pedal_Covers',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '4suumceyxio5-at4-regular-cab': {
          view: 'interior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Pedal_Covers_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Pedal_Covers_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Pedal_Covers_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        dwhrgewq2lfe: {
          view: 'interior',
          groupId: 'at4-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Floor_Liners_CC_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Floor_Liners_CC_GMC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Floor_Liners_CC_GMC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'dwhrgewq2lfe-at4-regular-cab': {
          view: 'interior',
          groupId: 'at4-regular-cab-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Floor_Liners_RC_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Floor_Liners_RC_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Floor_Liners_RC_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        tj2jpobpx5dp: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Hard_Folding_Tonneau_Cover_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Hard_Folding_Tonneau_Cover_CC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Hard_Folding_Tonneau_Cover_CC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'tj2jpobpx5dp-at4-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Hard_Folding_Tonneau_Cover_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Hard_Folding_Tonneau_Cover_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Hard_Folding_Tonneau_Cover_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        rxm16nzhijdr: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'rxm16nzhijdr-at4-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Sports_Bar_Bed_Mounted_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Sports_Bar_Bed_Mounted_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Sports_Bar_Bed_Mounted_RC',
              identifiers: [
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        es6d42r8m4tu: {
          view: 'exterior',
          groupId: 'at4-g2',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Machined_Aluminum_Carbon_Grey_Metallic',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Wheel',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_20inch_wheel',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'es6d42r8m4tu-at4-regular-cab': {
          view: 'exterior',
          groupId: 'at4-regular-cab-g2',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Machined_Aluminum_Carbon_Grey_Metallic',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Wheel',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_20inch_wheel',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist2ouuttdgpfud: {
          view: 'exterior',
          groupId: 'at4-g3',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Steel_Cargo_bed',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist2ouuttdgpfud-at4-regular-cab': {
          view: 'exterior',
          groupId: 'at4-regular-cab-g3',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Steel_Cargo_bed',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist2ouuptdgpfud: {
          view: 'exterior',
          groupId: 'at4-g3',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Bed_Liner_Spray_on',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Cargo_Bed',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist2ouuptdgpfud-at4-regular-cab': {
          view: 'exterior',
          groupId: 'at4-regular-cab-g3',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Bed_Liner_Spray_on',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Cargo_Bed',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist2otuptdgpfud: {
          view: 'exterior',
          groupId: 'at4-g4',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: '5.3L_Engine',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Cargo_Bed',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist2otuptdgpfud-at4-regular-cab': {
          view: 'exterior',
          groupId: 'at4-regular-cab-g4',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: '5.3L_Engine',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Cargo_Bed',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist2omuptygpfud: {
          view: 'exterior',
          groupId: 'at4-g4',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: '6.2L_Engine',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist2omuptygpfud-at4-regular-cab': {
          view: 'exterior',
          groupId: 'at4-regular-cab-g4',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: '6.2L_Engine',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist2omnuptygpfud: {
          view: 'exterior',
          groupId: 'at4-g5',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Standard_Roof_AT4',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist2omnupptygpfud: {
          view: 'exterior',
          groupId: 'at4-g5',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Sunroof_AT4',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Roof',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Sunroof',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist2onnupptygpfud: {
          view: 'exterior',
          groupId: 'at4-g6',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Technology_Package_Denali',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist2onnpptygpfud: {
          view: 'exterior',
          groupId: 'at4-g6',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Technology_Package_AT4_Denali',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8s2wi4e4vhfs': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Elevation_EXT_Variant',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '9q3c75v5lpjh': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Elevation_Short_Bed',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '9q3c75v5lpjh-elevation-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Elevation_STD_Bed',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ctiiaau8rxez: {
          view: 'interior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Floor_Mat_Rubber_Brown',
              identifiers: [
                'common_exterior',
                'optional',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ctiiaau8rxez-elevation-regular-cab': {
          view: 'interior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Floor_Mat_Rubber_Brown',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '2thmtg046cnh': {
          view: 'exterior',
          groupId: 'elevation-regular-cab-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Tailgate_Std',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '2thmtg046cnh-elevation': {
          view: 'exterior',
          // Was 'elevation-regular-cab-g0' (the shared default's own
          // group), which only pairs with elevation-regular-cab's own
          // GMC MultiPro Tailgate - plain Elevation's own MultiPro Tailgate
          // (w6212zibw7u2, groupId 'elevation-g0') was never in the same
          // group as this default, so picking it never evicted the
          // default - both rendered at once, visually overlapping.
          groupId: 'elevation-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Tailgate_Std',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        w6212zibw7u2: {
          view: 'exterior',
          groupId: 'elevation-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Tailgate_Pro',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_TAILGATE',
              identifiers: [
                'common_exterior',
                'optional',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Tailgate',
              identifiers: [
                'exterior-color',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            wbs3r7ayeu0p: { add: false, snapToLastFrame: true },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'w6212zibw7u2-elevation-regular-cab': {
          view: 'exterior',
          groupId: 'elevation-regular-cab-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Tailgate_Pro',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_TAILGATE',
              identifiers: [
                'common_exterior',
                'optional',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Tailgate',
              identifiers: [
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            wbs3r7ayeu0p: { add: false, snapToLastFrame: true },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        y8ymiarprmv4: {
          view: 'exterior',
          groupId: 'elevation-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Turbomax_Engine',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'y8ymiarprmv4-elevation-regular-cab': {
          view: 'exterior',
          groupId: 'elevation-regular-cab-g1',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'Turbomax_Engine',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        d5a3wq648mre: {
          view: 'exterior',
          groupId: 'elevation-g1',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: '5.3L_Engine',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'd5a3wq648mre-elevation-regular-cab': {
          view: 'exterior',
          groupId: 'elevation-regular-cab-g1',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: '5.3L_Engine',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        rx3fxh4w00fa: {
          view: 'exterior',
          groupId: 'elevation-g2',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Camera_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            '80whon9hvq2k': {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'rx3fxh4w00fa-elevation-regular-cab': {
          view: 'exterior',
          groupId: 'elevation-regular-cab-g2',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Camera_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            '80whon9hvq2k': {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        mwe92ojlfc9p: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Bed_Mat_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Bed_Mat_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Bed_Mat_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'mwe92ojlfc9p-elevation-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Bed_Mat_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Bed_Mat_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Bed_Mat_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '3sjpe1de9fow': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Illuminated_GMC_Logo_Black_SLE_ELEVATION',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Illuminated_GMC_Logo_Black_SLE_ELEVATION',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Illuminated_GMC_Logo_Black_SLE_ELEVATION',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '3sjpe1de9fow-elevation-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Illuminated_GMC_Logo_Black_SLE_ELE_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Illuminated_GMC_Logo_Black_SLE_ELE_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Illuminated_GMC_Logo_Black_SLE_ELE_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        xr3j6cnduod7: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Led_Puddle_Light_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Led_Puddle_Light_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Led_Puddle_Light_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'xr3j6cnduod7-elevation-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Led_Puddle_Light_GMC_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Led_Puddle_Light_GMC_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Led_Puddle_Light_GMC_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '6ed04e5xsebb': {
          view: 'interior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Pedal_Covers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Pedal_Covers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Pedal_Covers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '6ed04e5xsebb-elevation-regular-cab': {
          view: 'interior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Pedal_Covers_RC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Pedal_Covers_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Pedal_Covers_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        lqug7is1eblt: {
          view: 'interior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Floor_Liners_CC_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Floor_Liners_CC_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Floor_Liners_CC_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'lqug7is1eblt-elevation-regular-cab': {
          view: 'interior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Floor_Liners_RC_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Floor_Liners_RC_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Floor_Liners_RC_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '7w4207uu0x8f': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Hard_Folding_Tonneau_Cover_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Hard_Folding_Tonneau_Cover_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Hard_Folding_Tonneau_Cover_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '7w4207uu0x8f-elevation-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Hard_Folding_Tonneau_Cover_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Hard_Folding_Tonneau_Cover_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Hard_Folding_Tonneau_Cover_RC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        hadg99yjs0k1: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'hadg99yjs0k1-elevation-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Sports_Bar_Bed_Mounted_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Sports_Bar_Bed_Mounted_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Sports_Bar_Bed_Mounted_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist22outtdgpfud: {
          view: 'exterior',
          groupId: 'elevation-g3',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Bed_View_Camera',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist22outtdgpfud-elevation-regular-cab': {
          view: 'exterior',
          groupId: 'elevation-regular-cab-g3',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'WO_Bed_View_Camera',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist23outtdgpfud: {
          view: 'exterior',
          groupId: 'elevation-g3',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Bed_View_Camera',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Roof_Light',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Roof_Light',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist23outtdgpfud-elevation-regular-cab': {
          view: 'exterior',
          groupId: 'elevation-regular-cab-g3',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Bed_View_Camera',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Roof_Light',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Roof_Light',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist23ouuttdgpfud: {
          view: 'exterior',
          groupId: 'elevation-regular-cab-g4',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Machined_Aluminum_Dark_Painted_Pockets',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist23ouuttdgpfud-elevation': {
          view: 'exterior',
          // Was 'elevation-regular-cab-g4' (the shared default's own
          // group), which only pairs with elevation-regular-cab's wheels -
          // plain Elevation's own wheels (ist21ouuptdgpfud/ist29ouuptdgpfud,
          // groupId 'elevation-g4') were never in the same group as this
          // default, so picking either never evicted it - both rendered at
          // once, visually overlapping.
          groupId: 'elevation-g4',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Machined_Aluminum_Dark_Painted_Pockets',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist21ouuptdgpfud: {
          view: 'exterior',
          groupId: 'elevation-g4',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: '6_Spoke_Machined_Aluminum',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Wheel',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_18inch_wheel',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist21ouuptdgpfud-elevation-regular-cab': {
          view: 'exterior',
          groupId: 'elevation-regular-cab-g4',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: '6_Spoke_Machined_Aluminum',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Wheel',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_18inch_wheel',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist29ouuptdgpfud: {
          view: 'exterior',
          groupId: 'elevation-g4',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Black_Gloss_Painted_Aluminium',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Wheel',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_20inch_wheel',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist29ouuptdgpfud-elevation-regular-cab': {
          view: 'exterior',
          groupId: 'elevation-regular-cab-g4',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Black_Gloss_Painted_Aluminium',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Wheel',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_20inch_wheel',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist39ouuptdgpfud: {
          view: 'exterior',
          groupId: 'elevation-regular-cab-g5',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Steel_Cargo_bed',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist39ouuptdgpfud-elevation': {
          view: 'exterior',
          // Was 'elevation-regular-cab-g5' (the shared default's own
          // group), which only pairs with elevation-regular-cab's own
          // Bed Liner, Spray-on - plain Elevation's own Bed Liner
          // (ist49ouuptdgpfud, groupId 'elevation-g5') was never in the
          // same group as this default, so picking it never evicted the
          // default - both rendered at once, visually overlapping.
          groupId: 'elevation-g5',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Steel_Cargo_bed',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist49ouuptdgpfud: {
          view: 'exterior',
          groupId: 'elevation-g5',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Bed_Liner_Spray_on',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Cargo_Bed',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist49ouuptdgpfud-elevation-regular-cab': {
          view: 'exterior',
          groupId: 'elevation-regular-cab-g5',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Bed_Liner_Spray_on',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Cargo_Bed',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        t7wjlog6bvl5: {
          view: 'exterior',
          groupId: 'elevation-g6',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Heated_Seat_Steering',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        't7wjlog6bvl5-elevation-regular-cab': {
          view: 'exterior',
          groupId: 'elevation-regular-cab-g6',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Heated_Seat_Steering',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        isu929opuptdgpfu2: {
          view: 'interior',
          groupId: 'elevation-g7',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Bucket_Console_Cloth',
              identifiers: [
                'common_exterior',
                'optional',
              ],
              condition: '(5913) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'Bucket_Console_Leather',
              identifiers: [
                'common_exterior',
                'optional',
              ],
              condition: '(5921) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'isu929opuptdgpfu2-elevation-regular-cab': {
          view: 'interior',
          groupId: '',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'Bucket_Console_Cloth',
              identifiers: [
                'common_exterior',
                'optional',
              ],
              condition: '(5913) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
            {
              message: 'Bucket_Console_Leather',
              identifiers: [
                'common_exterior',
                'optional',
              ],
              condition: '(5921) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        v2vjnw0hmy80: {
          view: 'interior',
          groupId: 'elevation-g6',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Heated_Seat_Steering',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_STEERING',
              identifiers: [
                'exterior-color',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'v2vjnw0hmy80-elevation-regular-cab': {
          view: 'interior',
          groupId: 'elevation-regular-cab-g6',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Heated_Seat_Steering',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_STEERING',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist79ouuptdgpfud: {
          view: 'interior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Bose_Speakers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Bose_logo',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist79ouuptdgpfud-elevation-regular-cab': {
          view: 'interior',
          groupId: '',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'Bose_Speakers',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Bose_logo',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ctjy9mi8svfw: {
          view: 'exterior',
          groupId: 'elevation-g2',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Assist_Steps_BVT',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Assist_Steps',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Assist_Steps_BVT',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ctjy9mi8svfw-elevation-regular-cab': {
          view: 'exterior',
          groupId: 'elevation-regular-cab-g2',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'Assist_Steps_BVT',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Assist_Steps',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Assist_Steps_BVT',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist199ouuptdgpfud: {
          view: 'exterior',
          groupId: 'elevation-regular-cab-g7',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Z71_Suspension_Package_ELE',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        isu99ouuptdgpfud: {
          view: 'exterior',
          groupId: 'elevation-regular-cab-g7',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Z71_Suspension_Package_ELE',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
            'elevation-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        isu929opuptdgpfud: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Driver_Alert_Package_ELE',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        isu929opuptdgpfu1: {
          view: 'interior',
          groupId: 'elevation-g7',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Bench_Console_Cloth',
              identifiers: [
                'common_exterior',
                'optional',
              ],
              condition: '(5913) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'Bench_Console_Leather',
              identifiers: [
                'common_exterior',
                'optional',
              ],
              condition: '(5921) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'elevation',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '3w06jsi5oinw': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'SLE_EXT_Variant',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        whv4p5vjna3p: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'SLE_Short_Bed',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'whv4p5vjna3p-sle-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'SLE_STD_Bed',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        dpsosp5gxrx3: {
          view: 'interior',
          // Was 'sle-g0', colliding with the unrelated Polished Aluminum
          // Technical Grey Painted wheel (isd8oqfmgpfud) - both sharing a
          // group meant selecting the wheel would silently strip this floor
          // mat default via the SDK's group-exclusivity cleanup. Same bug
          // class already fixed for sle-regular-cab (see dpsosp5gxrx3's own
          // -sle-regular-cab sibling below, on 'sle-regular-cab-g8').
          groupId: 'sle-g9',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Floor_Mat_Rubber_Brown',
              identifiers: [
                'common_exterior',
                'optional',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'dpsosp5gxrx3-sle-regular-cab': {
          view: 'interior',
          // Was 'sle-regular-cab-g0', colliding with the unrelated wheel
          // pair (9kq6x8627fks/isd8oqfmgpfud-sle-regular-cab) below - both
          // this and the wheel default were isDefault:true in the same
          // group, so whichever applied last (file order) evicted this
          // floor mat via the SDK's group-exclusivity cleanup.
          groupId: 'sle-regular-cab-g8',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Floor_Mat_Rubber_Brown',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        prnt5nsa6jxn: {
          view: 'exterior',
          groupId: 'sle-regular-cab-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Tailgate_Std',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '9kq6x8627fks': {
          view: 'exterior',
          groupId: 'sle-regular-cab-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: '6_Spoke_Machined_Aluminum',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '9kq6x8627fks-sle': {
          view: 'exterior',
          // Was 'sle-regular-cab-g0' (the shared default's own group),
          // which only pairs with sle-regular-cab's own wheel
          // (isd8oqfmgpfud-sle-regular-cab) - plain SLE's own wheel
          // (isd8oqfmgpfud, groupId 'sle-g0') was never in the same group
          // as this default, so picking it never evicted the default -
          // both rendered at once, visually overlapping.
          groupId: 'sle-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: '6_Spoke_Machined_Aluminum',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        isd8oqmgpfud: {
          view: 'exterior',
          groupId: 'sle-regular-cab-g1',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Tailgate_Pro',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_TAILGATE',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Tailgate',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            smjhed8evi5i: {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        isd8oqfmgpfud: {
          view: 'exterior',
          groupId: 'sle-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Polished_Aluminum_Technical_Grey_Painted',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Wheel',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_20inch_wheel',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'isd8oqfmgpfud-sle-regular-cab': {
          view: 'exterior',
          groupId: 'sle-regular-cab-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Polished_Aluminum_Technical_Grey_Painted',
              identifiers: [
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Wheel',
              identifiers: [
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_20inch_wheel',
              identifiers: [
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        isd8oqfmdgpfud: {
          view: 'exterior',
          groupId: 'sle-g2',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Camera_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            nw7ayiomff7o: {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'isd8oqfmdgpfud-sle-regular-cab': {
          view: 'exterior',
          groupId: 'sle-regular-cab-g2',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Camera_Assist_Steps_CC_4in_Round_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            nw7ayiomff7o: {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        isd8oqftdgpfud: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Bed_Mat_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Bed_Mat_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Bed_Mat_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'isd8oqftdgpfud-sle-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Bed_Mat_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Bed_Mat_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Bed_Mat_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        isd8oqytdgpfud: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Illuminated_GMC_Logo_Black_SLE_ELEVATION',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Illuminated_GMC_Logo_Black_SLE_ELEVATION',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Illuminated_GMC_Logo_Black_SLE_ELEVATION',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'isd8oqytdgpfud-sle-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Illuminated_GMC_Logo_Black_SLE_ELE_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Illuminated_GMC_Logo_Black_SLE_ELE_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Illuminated_GMC_Logo_Black_SLE_ELE_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist8oqytdgpfud: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Led_Puddle_Light_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Led_Puddle_Light_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Led_Puddle_Light_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist8oqytdgpfud-sle-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Led_Puddle_Light_GMC_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Led_Puddle_Light_GMC_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Led_Puddle_Light_GMC_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        o4zovzzvc244: {
          view: 'interior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Pedal_Covers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Pedal_Covers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Pedal_Covers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'o4zovzzvc244-sle-regular-cab': {
          view: 'interior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Pedal_Covers_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Pedal_Covers_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Pedal_Covers_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist8outtdgpfud: {
          view: 'interior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Floor_Liners_CC_GMC',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Floor_Liners_CC_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Floor_Liners_CC_GMC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist8outtdgpfud-sle-regular-cab': {
          view: 'interior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Floor_Liners_RC_Black',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Floor_Liners_RC_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Floor_Liners_RC_Black',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist6outtdgpfud: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Hard_Folding_Tonneau_Cover_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Hard_Folding_Tonneau_Cover_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Hard_Folding_Tonneau_Cover_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist6outtdgpfud-sle-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Hard_Folding_Tonneau_Cover_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Hard_Folding_Tonneau_Cover_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Hard_Folding_Tonneau_Cover_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist2outtdgpfud: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Sports_Bar_Bed_Mounted_CC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist2outtdgpfud-sle-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Sports_Bar_Bed_Mounted_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Sports_Bar_Bed_Mounted_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Sports_Bar_Bed_Mounted_RC',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist21outtdgpfud: {
          view: 'exterior',
          groupId: 'sle-g3',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Bed_View_Camera',
              identifiers: [
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist21outtdgpfud-sle-regular-cab': {
          view: 'exterior',
          groupId: 'sle-regular-cab-g3',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'WO_Bed_View_Camera',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist211outtdgpfud: {
          view: 'exterior',
          groupId: 'sle-g3',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Bed_View_Camera',
              identifiers: [
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Roof_Light',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Roof_Light',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist211outtdgpfud-sle-regular-cab': {
          view: 'exterior',
          groupId: 'sle-regular-cab-g3',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Bed_View_Camera',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Roof_Light',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Roof_Light',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist31outtdgpfud: {
          view: 'exterior',
          groupId: 'sle-g4',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Turbomax_Engine',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist31outtdgpfud-sle-regular-cab': {
          view: 'exterior',
          groupId: 'sle-regular-cab-g4',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'Turbomax_Engine',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist41outtdgpfud: {
          view: 'exterior',
          groupId: 'sle-g4',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: '5.3L_Engine',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Roof_Light',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Roof_Light',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist41outtdgpfud-sle-regular-cab': {
          view: 'exterior',
          groupId: 'sle-regular-cab-g4',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: '5.3L_Engine',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Roof_Light',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Roof_Light',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist51outtdgpfud: {
          view: 'exterior',
          groupId: 'sle-g5',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Steel_Cargo_bed',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist51outtdgpfud-sle-regular-cab': {
          view: 'exterior',
          groupId: 'sle-regular-cab-g5',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Steel_Cargo_bed',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist61outtdgpfud: {
          view: 'exterior',
          groupId: 'sle-g5',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Bed_Liner_Spray_on',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Cargo_Bed',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist61outtdgpfud-sle-regular-cab': {
          view: 'exterior',
          groupId: 'sle-regular-cab-g5',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Bed_Liner_Spray_on',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Cargo_Bed',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist59ouuptdgpfud: {
          view: 'exterior',
          groupId: 'sle-g6',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Heated_Seat_Steering',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist59ouuptdgpfud-sle-regular-cab': {
          view: 'exterior',
          groupId: 'sle-regular-cab-g6',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Heated_Seat_Steering',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist890ouuptdgpfud: {
          view: 'interior',
          groupId: 'sle-g7',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Bucket_Console_Cloth',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Center_Console',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist890ouuptdgpfud-sle-regular-cab': {
          view: 'interior',
          groupId: '',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'Bucket_Console_Cloth',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Center_Console',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist69ouuptdgpfud: {
          view: 'interior',
          groupId: 'sle-regular-cab-g6',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Heated_Seat_Steering',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_STEERING',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist89ouuptdgpfud: {
          view: 'exterior',
          groupId: 'sle-g2',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Assist_Steps_BVT',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Assist_Steps',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Assist_Steps_BVT',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist89ouuptdgpfud-sle-regular-cab': {
          view: 'exterior',
          groupId: 'sle-regular-cab-g2',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'Assist_Steps_BVT',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_Assist_Steps',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Assist_Steps_BVT',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist99ouuptdgpfud: {
          view: 'exterior',
          groupId: 'sle-g7',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Bench_Console_Cloth',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist99ouuptdgpfud-sle-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'Bench_Console_Cloth',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ist99o1uuptdgpfud: {
          view: 'exterior',
          groupId: 'sle-g8',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Z71_Suspension_Package',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'ist99o1uuptdgpfud-sle-regular-cab': {
          view: 'exterior',
          groupId: 'sle-regular-cab-g7',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Z71_Suspension_Package',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        isu199ouuptdgpfud: {
          view: 'exterior',
          groupId: 'sle-g8',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Z71_Suspension_Package',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'isu199ouuptdgpfud-sle-regular-cab': {
          view: 'exterior',
          groupId: 'sle-regular-cab-g7',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Z71_Suspension_Package',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        isu299opuptdgpfud: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Driver_Alert_Package',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'isu299opuptdgpfud-sle-regular-cab': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'Driver_Alert_Package',
              identifiers: [
                'exterior-color',
                'optional',
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sle-regular-cab',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
      },
      packages: {
        d32e9gl7iz90: {
          groupId: 'denali-pkg0',
          accessoryIds: [
            'ist2outyudgpfud',
          ],
          dependencyPackages: [],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
          relevantVariants: [
            'denali',
          ],
        },
        v8j6f45my7e5: {
          groupId: 'at4-pkg0',
          accessoryIds: [
            'ist2onnpptygpfud',
          ],
          dependencyPackages: [],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
          relevantVariants: [
            'at4',
          ],
        },
        gwhmcx9hkln4: {
          groupId: 'elevation-regular-cab-pkg0',
          accessoryIds: [
            'isu99ouuptdgpfud',
          ],
          dependencyPackages: [],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
          relevantVariants: [
            'elevation-regular-cab',
          ],
        },
        x7vtyriufcgr0: {
          groupId: 'sle-regular-cab-pkg0',
          accessoryIds: [
            'isu199ouuptdgpfud-sle-regular-cab',
          ],
          dependencyPackages: [],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
          relevantVariants: [
            'sle-regular-cab',
          ],
        },
        'x7vtyriufcgr0-sle': {
          groupId: 'sle-pkg0',
          accessoryIds: [
            'isu199ouuptdgpfud',
          ],
          dependencyPackages: [],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
          relevantVariants: [
            'sle',
          ],
        },
      },
      interactions: {
        ywx48hyqwid8: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 5.0385,
                beta: 1.448,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            'denali-ultimate',
          ],
        },
        '2blnjd8gu2v1': {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'denali-ultimate',
          ],
        },
        '9g9a5bgi68eq': {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7049,
                beta: 0.115,
                radius: 3.2,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.7145,
                beta: 3.1212,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.7222,
                beta: 1.8799,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 4.7036,
                beta: 1.5732,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            'denali-ultimate',
          ],
        },
        '3f6680lmbg8l': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'denali-ultimate',
          ],
        },
        lt2y60dndnd0: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'denali-ultimate',
          ],
        },
        dxynjw4qubax: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'denali-ultimate',
          ],
          glowMesh: {
            materialName: 'EXT_TAILLIGHT_GLOW_02',
            identifiers: [
              'exterior-color',
            ],
            glow_on: [1, 0.35, 0],
            glow_off: [0.8, 0.02, 0],
          },
        } as any,
        '8eaoh15qfyy8': {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 5.0385,
                beta: 1.448,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            'denali',
          ],
        },
        qwl8becl9sgr: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'denali',
          ],
        },
        gjxwo8tfzu4z: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7049,
                beta: 0.115,
                radius: 3.2,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.7145,
                beta: 3.1212,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.7222,
                beta: 1.8799,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 4.7036,
                beta: 1.5732,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            'denali',
          ],
        },
        rsofu3ns0i6i: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'denali',
          ],
        },
        ln2vk13w0eev: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'denali',
          ],
        },
        '26yxlulgpwuk': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'denali',
          ],
          glowMesh: {
            materialName: 'EXT_TAILLIGHT_GLOW_02',
            identifiers: [
              'exterior-color',
            ],
            glow_on: [1, 0.35, 0],
            glow_off: [0.8, 0.02, 0],
          },
        } as any,
        jkzo5nb4kcu8: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 5.0385,
                beta: 1.448,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            'at4x',
          ],
        },
        '7d69b0ehn4v1': {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'at4x',
          ],
        },
        l3grk49kb6tp: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7049,
                beta: 0.115,
                radius: 3.2,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.7145,
                beta: 3.1212,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.7222,
                beta: 1.8799,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 4.7036,
                beta: 1.5732,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            'at4x',
          ],
        },
        '71pzl0ulzqds': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'at4x',
          ],
        },
        xli2gxmrn457: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'at4x',
          ],
        },
        ckupcfxwja4s: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'at4x',
          ],
          glowMesh: {
            materialName: 'EXT_TAILLIGHT_GLOW_02',
            identifiers: [
              'exterior-color',
            ],
            glow_on: [1, 0.35, 0],
            glow_off: [0.8, 0.02, 0],
          },
        } as any,
        ttgcv0vc2ypv: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 5.0385,
                beta: 1.448,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            'at4',
            'at4-regular-cab',
          ],
        },
        vbjrc2b3qn47: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'at4',
            'at4-regular-cab',
          ],
        },
        obp0p5qsd1a3: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7049,
                beta: 0.115,
                radius: 3.2,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.7145,
                beta: 3.1212,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.7222,
                beta: 1.8799,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 4.7036,
                beta: 1.5732,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            'at4',
            'at4-regular-cab',
          ],
          // Sunroof isn't standard on this trim - only show/enable this
          // interaction once the user has actually applied the Power
          // Sunroof accessory, instead of unconditionally per
          // relevantVariants+view.
          availableFor: {
            accessories: ['ist2omnupptygpfud'],
          },
        },
        '8wx307el85j4': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'at4',
            'at4-regular-cab',
          ],
        },
        vdknirw6a06i: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'at4',
            'at4-regular-cab',
          ],
        },
        ovfziv1t5iu0: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'at4',
            'at4-regular-cab',
          ],
          glowMesh: {
            materialName: 'EXT_TAILLIGHT_GLOW_02',
            identifiers: [
              'exterior-color',
            ],
            glow_on: [1, 0.35, 0],
            glow_off: [0.8, 0.02, 0],
          },
        } as any,
        '80whon9hvq2k': {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 5.0385,
                beta: 1.448,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            'elevation',
            'elevation-regular-cab',
          ],
        },
        wbs3r7ayeu0p: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'elevation',
            'elevation-regular-cab',
          ],
        },
        zrmrylhs1tvw: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'elevation',
            'elevation-regular-cab',
          ],
        },
        n0d9xags2ma4: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'elevation',
            'elevation-regular-cab',
          ],
        },
        efpnxgfbnbyi: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'elevation',
            'elevation-regular-cab',
          ],
          glowMesh: {
            materialName: 'EXT_TAILLIGHT_GLOW_02',
            identifiers: [
              'exterior-color',
            ],
            glow_on: [1, 0.35, 0],
            glow_off: [0.8, 0.02, 0],
          },
        } as any,
        nw7ayiomff7o: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 5.0385,
                beta: 1.448,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            'sle',
            'sle-regular-cab',
          ],
        },
        smjhed8evi5i: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'sle',
            'sle-regular-cab',
          ],
        },
        z50uy8f03jh7: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'sle',
            'sle-regular-cab',
          ],
        },
        '14eisbljp9pi': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'sle',
            'sle-regular-cab',
          ],
        },
        '1vqrly7ngbhv': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'sle',
            'sle-regular-cab',
          ],
          glowMesh: {
            materialName: 'EXT_TAILLIGHT_GLOW_02',
            identifiers: [
              'exterior-color',
            ],
            glow_on: [1, 0.35, 0],
            glow_off: [0.8, 0.02, 0],
          },
        } as any,
      },
      hotspots: {
        exteriorCamera: {
          type: 'exterior',
          messages: {
            fov: 0.8,
            alpha: 2.02,
            beta: 1.47,
            radius: 5.7,
            lowerAlphaLimit: null,
            upperAlphaLimit: null,
            lowerBetaLimit: 0.1,
            upperBetaLimit: 1.6,
            lowerRadiusLimit: 3.2,
            upperRadiusLimit: 6.7,
            target: {
              x: 0,
              y: 0.65,
              z: 0,
            },
            isFovZoomModeOn: false,
          },
        },
        interiorCamera: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA',
            identifiers: [
              'common_exterior',
            ],
          },
        },
        interiorCamera_1: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA_2',
            identifiers: [
              'common_exterior',
            ],
          },
        },
        interiorCamera_2: {
          type: 'interior',
          messages: {},
        },
      },
      usdzPathWithVariable: '',
    },
  },
};
